/**
 * Campaign attribution: which link a visitor arrived on (utm_* parameters).
 *
 * The A3 posters carry a QR code to /beta?utm_source=poster&utm_medium=print&utm_campaign=beta_a3.
 * The parameters are lost as soon as the visitor moves to another page, so the first page load
 * stores them here and the beta form reads them back when it submits. Kept 30 days in
 * localStorage, so someone who scans today and signs up next week is still counted.
 * Storage can be blocked (private windows), so every access is guarded and failure means
 * "no campaign", never an error.
 */

const KEY = "askfinz_campaign";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export type Campaign = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingPath?: string;
  referrer?: string;
};

const clip = (v: string | null | undefined, n: number) => (v ? v.slice(0, n) : undefined);

/** Read utm_* from the current URL. Returns null when the page has none. */
export function campaignFromUrl(): Campaign | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const c: Campaign = {
    utmSource: clip(q.get("utm_source"), 64),
    utmMedium: clip(q.get("utm_medium"), 64),
    utmCampaign: clip(q.get("utm_campaign"), 64),
  };
  if (!c.utmSource && !c.utmMedium && !c.utmCampaign) return null;
  c.landingPath = clip(window.location.pathname, 256);
  c.referrer = clip(document.referrer, 512);
  return c;
}

export function saveCampaign(c: Campaign): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...c, at: Date.now() }));
  } catch {
    /* storage blocked: attribution is best-effort */
  }
}

/** The stored campaign, or an empty object when there is none or it has expired. */
export function readCampaign(): Campaign {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const { at, ...c } = JSON.parse(raw) as Campaign & { at?: number };
    if (!at || Date.now() - at > MAX_AGE_MS) return {};
    return c;
  } catch {
    return {};
  }
}
